import type {
  CreateNotificationTemplateRequest,
  CustomerCommunicationDto,
  NotificationDto,
  NotificationFailureReportDto,
  NotificationPreferencesDto,
  NotificationProcessResultDto,
  NotificationReportDto,
  NotificationSummaryDto,
  NotificationTemplateDto,
  NotificationTemplatePreviewDto,
  NotificationTemplateVariablesDto,
  NotificationUnreadCountDto,
  PortalNotificationDto,
  PortalNotificationListDto,
  PortalNotificationPreferencesDto,
  UpdateNotificationPreferencesRequest,
  UpdateNotificationTemplateRequest,
} from '@erp-boilerplate/types';

import { api, type QueryParams } from '@/lib/api-client';

/**
 * The notification API surface (Phase 7).
 *
 * Note the absence of a `send` or `create` method. There is no such endpoint —
 * a notification exists because a business transition succeeded — and the
 * service object is where somebody would look first for one, so its absence is
 * the clearest possible statement that no such thing exists.
 *
 * `retry` and `processPending` are the only two calls that cause a message to
 * move, and neither can invent one.
 */

export const notificationsService = {
  list: (params: QueryParams) => api.list<NotificationSummaryDto>('/notifications', params),
  getById: (id: string) => api.get<NotificationDto>(`/notifications/${id}`),

  /** The bell badge. Counts only rows addressed to the signed-in account. */
  unreadCount: () => api.get<NotificationUnreadCountDto>('/notifications/unread-count'),
  markRead: (id: string) => api.post<{ updated: number }>(`/notifications/${id}/read`, {}),
  markAllRead: () => api.post<{ updated: number }>('/notifications/read-all', {}),

  /** Requeue failed deliveries. Never touches one that already went out. */
  retry: (id: string, channel?: string) =>
    api.post<NotificationDto>(`/notifications/${id}/retry`, channel ? { channel } : {}),
  cancel: (id: string, reason: string) =>
    api.post<NotificationDto>(`/notifications/${id}/cancel`, { reason }),

  processPending: (limit?: number) =>
    api.post<NotificationProcessResultDto>(
      '/notifications/process-pending',
      limit ? { limit } : {},
    ),

  /** Raise today's overdue reminders. Consumes the Phase 4D overdue rule. */
  generateOverdue: (asOf?: string) =>
    api.post<{ businessDate: string; created: number; deduped: number }>(
      '/notifications/generate-overdue',
      asOf ? { asOf } : {},
    ),

  /* ---- Reports ---- */

  summary: (params: QueryParams) =>
    api.get<NotificationReportDto>('/notifications/reports/summary', params),
  failures: (params: QueryParams) =>
    api.get<NotificationFailureReportDto>('/notifications/reports/failures', params),

  /* ---- Templates ---- */

  templates: (includeSystem = true) =>
    api.get<NotificationTemplateDto[]>('/notifications/templates', {
      includeSystem: String(includeSystem),
    }),
  templateVariables: () =>
    api.get<NotificationTemplateVariablesDto[]>('/notifications/templates/variables'),
  template: (id: string) => api.get<NotificationTemplateDto>(`/notifications/templates/${id}`),
  createTemplate: (payload: CreateNotificationTemplateRequest) =>
    api.post<NotificationTemplateDto>('/notifications/templates', payload),
  updateTemplate: (id: string, payload: UpdateNotificationTemplateRequest) =>
    api.patch<NotificationTemplateDto>(`/notifications/templates/${id}`, payload),
  previewTemplate: (payload: CreateNotificationTemplateRequest) =>
    api.post<NotificationTemplatePreviewDto>('/notifications/templates/preview', payload),

  /* ---- Customer-scoped ---- */

  customerCommunication: (customerId: string, params: QueryParams) =>
    api.get<CustomerCommunicationDto>(`/customers/${customerId}/communication`, params),
  customerPreferences: (customerId: string) =>
    api.get<NotificationPreferencesDto>(`/customers/${customerId}/notification-preferences`),
  updateCustomerPreferences: (
    customerId: string,
    payload: UpdateNotificationPreferencesRequest,
  ) =>
    api.put<NotificationPreferencesDto>(
      `/customers/${customerId}/notification-preferences`,
      payload,
    ),
};

/**
 * The portal half.
 *
 * Kept in this file rather than `portal.service.ts` so the two halves of one
 * domain stay together, but note the shapes are different types entirely — the
 * customer's list carries no provider, no attempt count and no failure code.
 */
export const portalNotificationsService = {
  list: (params: QueryParams) =>
    api.get<PortalNotificationListDto>('/portal/notifications', params),
  getById: (id: string) => api.get<PortalNotificationDto>(`/portal/notifications/${id}`),
  unreadCount: () => api.get<{ unread: number }>('/portal/notifications/unread-count'),
  markRead: (id: string) =>
    api.post<PortalNotificationDto>(`/portal/notifications/${id}/read`, {}),
  markAllRead: () => api.post<{ updated: number }>('/portal/notifications/read-all', {}),

  preferences: () => api.get<PortalNotificationPreferencesDto>('/portal/preferences'),
  updatePreferences: (payload: UpdateNotificationPreferencesRequest) =>
    api.put<PortalNotificationPreferencesDto>('/portal/preferences', payload),
};
